import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FilePlus, HandCoins, FileText, CreditCard, ArrowLeftRight, Repeat, TrendingUp,
  ClipboardCheck, ClipboardList, BarChart3, CalendarRange, ShoppingCart, Scale, Receipt,
  Store, ChevronRight, AlertTriangle, Target, Check, ChevronDown, Pencil
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, formatHora, hoyCaracas } from '../utils/format';
import TarjetaDisponibilidad from '../components/TarjetaDisponibilidad';
import TarjetaPagosProximos from '../components/TarjetaPagosProximos';
import TarjetaAlertas from '../components/TarjetaAlertas';
import ElegirParte from '../components/ElegirParte';
import EditarMetas, { NOMBRES_META } from '../components/EditarMetas';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Pantalla Inicio (rediseño aprobado por Armando, 12/10/2026; maqueta en
// analisis/rediseno-inicio-menu-2026-10-12.md). Lo del día a día a un clic:
// - Acciones rápidas según rol, módulos y secciones del usuario (las mismas
//   reglas que las rutas de App.jsx). En restaurantes, la Caja va primero.
// - Disponibilidad con el saldo de cada cuenta a la vista, Alertas y Pagos
//   próximos (las tarjetas del Dashboard).
// - Reportes principales con su cifra clave (/inicio/reportes, en BCV$).
// El Dashboard de siempre (gráficos) quedó en Bancos → Resumen.
// ============================================================

const JERARQUIA = ['consultor', 'operador', 'contador', 'gerente', 'admin'];

const TINTES = {
  azul: 'bg-blue-50 text-blue-700',
  verde: 'bg-green-50 text-green-700',
  naranja: 'bg-orange-50 text-orange-700',
  rosa: 'bg-rose-50 text-rose-700',
  gris: 'bg-slate-100 text-slate-700',
  turquesa: 'bg-turquoise-50 text-turquoise-600',
  violeta: 'bg-violet-50 text-violet-700'
};

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const nombreMes = (aaaamm) => MESES[Number(String(aaaamm).slice(5, 7)) - 1] || '';
const bcv = (v) => `BCV$ ${formatMonto(v)}`;
const conSigno = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}BCV$ ${formatMonto(Math.abs(v))}`;
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Acciones rápidas. `req` = mismo rol mínimo, módulo y sección que su ruta.
// `parte`: antes de abrir hay que elegir cliente o proveedor.
const ACCIONES = {
  cerrar_turno: { label: 'Cerrar turno', sub: 'cierre y arqueo', icon: ClipboardCheck, tinte: 'turquesa', req: { rol: 'operador', modulo: 'caja' }, ir: ['/caja', { state: { accion: 'cerrar_turno' } }] },
  resumen_dia: { label: 'Resumen del día', sub: 'ventas de caja', icon: ClipboardList, tinte: 'azul', req: { rol: 'operador', modulo: 'caja' }, ir: ['/caja/resumen'] },
  factura_venta: { label: 'Nueva factura', sub: 'de venta', icon: FilePlus, tinte: 'azul', req: { rol: 'operador', modulo: 'ventas' }, parte: 'cliente', titulo: 'Nueva factura de venta: elige el cliente' },
  cobranza: { label: 'Registrar cobranza', sub: 'de un cliente', icon: HandCoins, tinte: 'verde', req: { rol: 'operador', modulo: 'ventas' }, parte: 'cliente', titulo: 'Registrar cobranza: elige el cliente' },
  factura_compra: { label: 'Nueva factura de compra', sub: 'a mano o por foto (IA)', icon: FileText, tinte: 'naranja', req: { rol: 'operador', modulo: 'proveedores' }, parte: 'proveedor', titulo: 'Nueva factura de compra', conFoto: true },
  pago_proveedor: { label: 'Registrar pago', sub: 'a un proveedor', icon: CreditCard, tinte: 'rosa', req: { rol: 'operador', modulo: 'proveedores' }, parte: 'proveedor', titulo: 'Registrar pago: elige el proveedor' },
  movimiento: { label: 'Nuevo movimiento bancario', sub: 'ingreso o egreso', icon: ArrowLeftRight, tinte: 'gris', req: { rol: 'operador', seccion: 'movimientos' }, ir: ['/movimientos?accion=nuevo'] },
  transferencia: { label: 'Transferencia', sub: 'entre cuentas', icon: Repeat, tinte: 'gris', req: { rol: 'operador', seccion: 'movimientos' }, ir: ['/movimientos?accion=transferencia'] },
  tasa: { label: 'Cargar tasa', sub: 'del día', icon: TrendingUp, tinte: 'turquesa', req: { rol: 'operador', seccion: 'tasas_cambio' }, ir: ['/tasas-cambio'] }
};
const ORDEN_ACCIONES = {
  restaurante: ['cerrar_turno', 'resumen_dia', 'factura_compra', 'pago_proveedor', 'movimiento', 'transferencia', 'tasa', 'factura_venta', 'cobranza'],
  otro: ['factura_venta', 'cobranza', 'factura_compra', 'pago_proveedor', 'movimiento', 'transferencia', 'tasa']
};

// Reportes principales: los mismos permisos que su ruta; se muestran los
// primeros 6 que el usuario puede abrir, en el orden de su rubro.
const REPORTES = {
  flujo_caja: { label: 'Flujo de caja', icon: BarChart3, tinte: 'turquesa', to: '/reportes/flujo-caja', req: { rol: 'consultor' } },
  flujo_proyectado: { label: 'Flujo proyectado', icon: CalendarRange, tinte: 'turquesa', to: '/reportes/flujo-proyectado', req: { rol: 'consultor' } },
  cxc: { label: 'Cuentas por cobrar', icon: HandCoins, tinte: 'verde', to: '/reportes/cuentas-por-cobrar', req: { rol: 'contador', modulo: 'reporte_cxc' } },
  cxp: { label: 'Cuentas por pagar', icon: CreditCard, tinte: 'rosa', to: '/reportes/cuentas-por-pagar', req: { rol: 'contador', modulo: 'reporte_cxp' } },
  ventas: { label: 'Dashboard de ventas', icon: ShoppingCart, tinte: 'azul', to: '/ventas', req: { rol: 'operador', modulo: 'ventas' } },
  resultados: { label: 'Estado de resultados', icon: Scale, tinte: 'violeta', to: '/reportes/estado-resultados', req: { rol: 'contador', seccion: 'movimientos' } },
  caja_dia: { label: 'Resumen del día', icon: ClipboardList, tinte: 'azul', to: '/caja/resumen', req: { rol: 'operador', modulo: 'caja' } },
  cierre_fiscal: { label: 'Cierre fiscal', icon: Receipt, tinte: 'turquesa', to: '/reportes/cierre-fiscal-diario', req: { rol: 'operador', modulo: 'caja' } }
};
const ORDEN_REPORTES = {
  restaurante: ['caja_dia', 'ventas', 'cierre_fiscal', 'flujo_caja', 'cxp', 'resultados', 'flujo_proyectado', 'cxc'],
  otro: ['flujo_caja', 'flujo_proyectado', 'cxc', 'cxp', 'ventas', 'resultados']
};

// Tareas del mes (parte 2): el estado lo calcula /inicio/tareas; acá el
// título, a dónde lleva y quién la ve (mismo rol/módulo que esa pantalla).
const TAREAS = {
  turno_sin_cerrar: { titulo: 'Cerrar el turno de caja', ir: 'Cerrar', to: ['/caja', { state: { accion: 'cerrar_turno' } }], req: { rol: 'operador', modulo: 'caja' } },
  cierre_fiscal_ayer: { titulo: 'Cierre fiscal de ayer', ir: 'Cargar', to: ['/reportes/cierre-fiscal-diario'], req: { rol: 'operador', modulo: 'caja' } },
  cobrar_vencidas: { titulo: 'Cobrar facturas vencidas', ir: 'Cobranza', to: ['/clientes'], req: { rol: 'operador', modulo: 'ventas' } },
  conciliar: { titulo: 'Conciliar las cuentas del mes', ir: 'Conciliar', to: ['/conciliacion'], req: { rol: 'contador', seccion: 'movimientos' } },
  pagar_agendado: { titulo: 'Pagar lo agendado esta semana', ir: 'Agenda', to: ['/reportes/cuentas-por-pagar'], req: { rol: 'contador', modulo: 'reporte_cxp' } },
  tasa_hoy: { titulo: 'Cargar la tasa del día', ir: 'Cargar', to: ['/tasas-cambio'], req: { rol: 'operador', seccion: 'tasas_cambio' } },
  cierre_inventario: { titulo: 'Cierre de inventario del mes', ir: 'Cerrar', to: ['/inventario/cierre'], req: { rol: 'contador', modulo: 'productos' } }
};
const ORDEN_TAREAS = ['turno_sin_cerrar', 'cierre_fiscal_ayer', 'cobrar_vencidas', 'conciliar', 'pagar_agendado', 'tasa_hoy', 'cierre_inventario'];


// Cifra y nota de cada reporte a partir de lo que manda el backend.
function cifraDe(clave, d) {
  if (!d || d.error) return { cifra: '—', nota: 'No se pudo calcular' };
  switch (clave) {
    case 'flujo_caja':
      return { cifra: conSigno(d.neto.bcv), nota: `Entradas menos salidas de ${nombreMes(d.mes)}` };
    case 'flujo_proyectado':
      return d.bajo_minimo
        ? { cifra: bcv(d.saldo_30?.bcv ?? 0), nota: `Cae bajo el mínimo el ${formatFecha(d.bajo_minimo.fecha)}`, alerta: true }
        : { cifra: bcv(d.saldo_30?.bcv ?? 0), nota: 'Saldo esperado a 30 días' };
    case 'cxc':
      return d.vencidas > 0
        ? { cifra: bcv(d.total), nota: `${plural(d.vencidas, 'factura vencida', 'facturas vencidas')} por ${bcv(d.vencido)}`, alerta: true }
        : { cifra: bcv(d.total), nota: d.documentos ? 'Nada vencido' : 'No hay nada por cobrar' };
    case 'cxp':
      if (d.vencidas > 0) return { cifra: bcv(d.total), nota: `${plural(d.vencidas, 'factura vencida', 'facturas vencidas')} por ${bcv(d.vencido)}`, alerta: true };
      if (d.proximas_7 > 0) return { cifra: bcv(d.total), nota: `${plural(d.proximas_7, 'vence', 'vencen')} en los próximos 7 días` };
      return { cifra: bcv(d.total), nota: d.documentos ? 'Nada vence esta semana' : 'No hay nada por pagar' };
    case 'ventas':
      return { cifra: bcv(d.total.bcv), nota: `${nombreMes(d.mes)} hasta hoy · ${plural(d.documentos, 'documento', 'documentos')}, sin IVA` };
    case 'resultados':
      return { cifra: conSigno(d.utilidad), nota: `${d.utilidad < 0 ? 'Pérdida' : 'Utilidad'} de ${nombreMes(d.mes)}`, alerta: d.utilidad < 0 };
    case 'caja_dia':
      return { cifra: bcv(d.total), nota: `${plural(d.tickets, 'ticket', 'tickets')} hoy, con IVA` };
    case 'cierre_fiscal':
      if (d.pendientes.length > 0) {
        return {
          cifra: plural(d.pendientes.length, 'día sin cargar', 'días sin cargar'),
          nota: `Falta el Z del ${d.pendientes.map((f) => formatFecha(f).slice(0, 5)).join(', ')}`,
          alerta: true
        };
      }
      return { cifra: 'Al día', nota: d.ultima ? `Último cargado: ${formatFecha(d.ultima)}` : 'Sin cierres cargados' };
    default:
      return { cifra: '—', nota: '' };
  }
}

function fechaLarga(iso) {
  const [a, m, d] = iso.split('-').map(Number);
  const dia = DIAS[new Date(a, m - 1, d).getDay()];
  return `${dia.charAt(0).toUpperCase()}${dia.slice(1)} ${d} de ${MESES[m - 1]}`;
}

export default function Inicio() {
  const navigate = useNavigate();
  const {
    usuario, empresaActiva, empresaActivaData, empresaActivaNombre, rolActual, esAdminGlobal,
    seccionesDeshabilitadas, modulosActivos
  } = useAuth();
  const rol = rolActual || (esAdminGlobal ? 'admin' : 'consultor');
  const rubro = empresaActivaData?.rubro === 'restaurante' ? 'restaurante' : 'otro';
  const puede = (req) =>
    JERARQUIA.indexOf(rol) >= JERARQUIA.indexOf(req.rol) &&
    (!req.modulo || !!modulosActivos?.[req.modulo]) &&
    (!req.seccion || !seccionesDeshabilitadas.includes(req.seccion));
  const verBancos = !seccionesDeshabilitadas.includes('dashboard');

  const acciones = ORDEN_ACCIONES[rubro].filter((k) => puede(ACCIONES[k].req));
  const reportes = ORDEN_REPORTES[rubro].filter((k) => puede(REPORTES[k].req)).slice(0, 6);
  const verCaja = rubro === 'restaurante' && puede(REPORTES.caja_dia.req);
  const clavesTareas = rol === 'consultor' ? [] : ORDEN_TAREAS.filter((k) => puede(TAREAS[k].req));
  const puedeEditarMetas = JERARQUIA.indexOf(rol) >= JERARQUIA.indexOf('gerente');
  const tiposMeta = ['ventas', 'cobranza', 'tope_compras', ...(rubro === 'restaurante' ? ['ticket_promedio'] : [])];

  const [cuentas, setCuentas] = useState(null);
  const [tasasHoy, setTasasHoy] = useState(null);
  const [cifras, setCifras] = useState(null);
  const [tareas, setTareas] = useState(null);
  const [metas, setMetas] = useState(null);
  const [editandoMetas, setEditandoMetas] = useState(false);
  const [recargaMetas, setRecargaMetas] = useState(0);
  const [eligiendo, setEligiendo] = useState(null); // clave de la acción que pide cliente/proveedor

  useEffect(() => {
    if (!empresaActiva) return;
    let vigente = true;
    setCifras(null);
    setCuentas(null);
    api.get('/tasas-cambio/hoy').then(({ data }) => { if (vigente) setTasasHoy(data); }).catch(() => { if (vigente) setTasasHoy(null); });
    if (verBancos) {
      api.get('/cuentas').then(({ data }) => { if (vigente) setCuentas(data); }).catch(() => { if (vigente) setCuentas([]); });
    }
    const claves = [...new Set([...reportes, ...(verCaja ? ['caja_dia'] : [])])];
    if (claves.length) {
      api.get('/inicio/reportes', { params: { claves: claves.join(',') } })
        .then(({ data }) => { if (vigente) setCifras(data.reportes); })
        .catch(() => { if (vigente) setCifras({}); });
    }
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, verBancos, verCaja, reportes.join(',')]);

  useEffect(() => {
    if (!empresaActiva || clavesTareas.length === 0) { setTareas(null); return undefined; }
    let vigente = true;
    setTareas(null);
    api.get('/inicio/tareas', { params: { claves: clavesTareas.join(',') } })
      .then(({ data }) => { if (vigente) setTareas(data.tareas); })
      .catch(() => { if (vigente) setTareas({}); });
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, clavesTareas.join(',')]);

  useEffect(() => {
    if (!empresaActiva) return undefined;
    let vigente = true;
    api.get('/inicio/metas')
      .then(({ data }) => { if (vigente) setMetas(data); })
      .catch(() => { if (vigente) setMetas(null); });
    return () => { vigente = false; };
  }, [empresaActiva, recargaMetas]);

  const verObjetivos = clavesTareas.length > 0 || puedeEditarMetas || (metas?.metas.length ?? 0) > 0;

  const faltaTasa = !(Number(tasasHoy?.tasa_bcv) > 0);

  function ejecutar(clave) {
    const a = ACCIONES[clave];
    if (a.parte) { setEligiendo(clave); return; }
    navigate(...a.ir);
  }

  function elegido(entidad) {
    const clave = eligiendo;
    setEligiendo(null);
    if (clave === 'factura_venta') navigate(`/clientes/${entidad.id}/factura/nueva`, { state: { entidad } });
    else if (clave === 'cobranza') navigate('/clientes', { state: { clienteId: entidad.id, accion: 'pago' } });
    else if (clave === 'factura_compra') navigate('/proveedores', { state: { proveedorId: entidad.id, accion: 'factura' } });
    else if (clave === 'pago_proveedor') navigate('/proveedores', { state: { proveedorId: entidad.id, accion: 'pago' } });
  }

  const nombre = String(usuario?.nombre || '').split(' ')[0];

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Hola{nombre ? `, ${nombre}` : ''}<BotonAyuda /></h1>
        <p className="text-sm text-slate-500 mt-0.5">{fechaLarga(hoyCaracas())}{empresaActivaNombre && ` · ${empresaActivaNombre}`}</p>
      </div>

      {verCaja && <BloqueCaja datos={cifras?.caja_dia} />}

      {acciones.length > 0 && (
        <section>
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">Acciones rápidas</h2>
          <div className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2.5">
            {acciones.map((k) => {
              const a = ACCIONES[k];
              const Icono = a.icon;
              const aviso = k === 'tasa' && faltaTasa;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => ejecutar(k)}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-blue-300 hover:bg-primary-50 transition-colors min-h-[56px]"
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${TINTES[a.tinte]}`}>
                    <Icono size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-800 leading-tight">{a.label}</span>
                    <span className={`block text-xs mt-0.5 ${aviso ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
                      {aviso ? 'falta la de hoy' : a.sub}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {verObjetivos ? (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)] gap-4 items-start">
          <BloqueObjetivos
            tareas={clavesTareas.length > 0 ? tareas : undefined}
            metas={metas}
            onEditarMetas={puedeEditarMetas ? () => setEditandoMetas(true) : null}
            navigate={navigate}
          />
          {verBancos && cuentas && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4 items-start">
              <TarjetaDisponibilidad cuentas={cuentas} tasasHoy={tasasHoy} enLinea />
              <TarjetaAlertas cuentas={cuentas} tasasHoy={tasasHoy} empresaActiva={empresaActiva} altoLibre />
              <TarjetaPagosProximos empresaActiva={empresaActiva} />
            </div>
          )}
        </div>
      ) : verBancos && cuentas && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          <TarjetaDisponibilidad cuentas={cuentas} tasasHoy={tasasHoy} enLinea />
          <TarjetaAlertas cuentas={cuentas} tasasHoy={tasasHoy} empresaActiva={empresaActiva} altoLibre />
          <TarjetaPagosProximos empresaActiva={empresaActiva} />
        </div>
      )}

      {reportes.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between gap-3 mb-2.5">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">Reportes principales</h2>
            {verBancos && (
              <Link to="/bancos/resumen" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                Resumen de Bancos con gráficos
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
            {reportes.map((k) => {
              const r = REPORTES[k];
              const Icono = r.icon;
              const c = cifras ? cifraDe(k, cifras[k]) : null;
              return (
                <Link
                  key={k}
                  to={r.to}
                  className="group flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition"
                >
                  <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TINTES[r.tinte]}`}>
                      <Icono size={17} />
                    </span>
                    <span className="flex-1">{r.label}</span>
                    <ChevronRight size={16} className="text-slate-400 group-hover:text-primary-600" />
                  </span>
                  {c ? (
                    <>
                      <span className="text-xl font-bold text-slate-800 tabular-nums">{c.cifra}</span>
                      <span className={`text-xs ${c.alerta ? 'font-medium text-amber-700' : 'text-slate-500'}`}>{c.nota}</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400 py-2">Cargando…</span>
                  )}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {editandoMetas && metas && (
        <EditarMetas
          anio={metas.anio}
          mes={metas.mes}
          tipos={tiposMeta}
          rubro={rubro}
          actuales={metas.metas}
          empresaNombre={empresaActivaNombre}
          onCerrar={() => setEditandoMetas(false)}
          onGuardado={() => { setEditandoMetas(false); setRecargaMetas((n) => n + 1); }}
        />
      )}

      {eligiendo && (
        <ElegirParte
          parte={ACCIONES[eligiendo].parte}
          titulo={ACCIONES[eligiendo].titulo}
          conFoto={!!ACCIONES[eligiendo].conFoto}
          onElegir={elegido}
          onFoto={(resultado) => { setEligiendo(null); navigate('/proveedores', { state: { iaResultado: resultado } }); }}
          onCerrar={() => setEligiendo(null)}
        />
      )}
    </div>
  );
}

// Restaurantes: estado del turno de caja y lo cobrado hoy (BCV$, con IVA).
const ESTILO_TAREA = {
  hecha: 'bg-turquoise-600 text-white',
  curso: 'bg-amber-100 text-amber-700',
  pendiente: 'bg-slate-100 text-slate-400',
  vencida: 'bg-red-100 text-red-700'
};

// Objetivos del mes: las tareas que calcula el sistema. Arriba las que
// faltan (las vencidas primero); las listas quedan tachadas y plegadas.
function BloqueObjetivos({ tareas, metas, onEditarMetas, navigate }) {
  const conTareas = tareas !== undefined;
  const [verListas, setVerListas] = useState(false);
  const mes = MESES[Number(hoyCaracas().slice(5, 7)) - 1];
  const filas = ORDEN_TAREAS.filter((k) => tareas?.[k] && !tareas[k].error).map((k) => ({ clave: k, ...TAREAS[k], ...tareas[k] }));
  const faltan = filas.filter((t) => t.estado !== 'hecha').sort((a, b) => (b.estado === 'vencida') - (a.estado === 'vencida'));
  const listas = filas.filter((t) => t.estado === 'hecha');
  const total = filas.length;

  const fila = (t) => {
    const hecha = t.estado === 'hecha';
    return (
      <button
        key={t.clave}
        type="button"
        onClick={() => navigate(...t.to)}
        className="w-full flex items-center gap-3 px-1 py-2.5 border-t border-slate-100 text-left hover:bg-slate-50 rounded-md min-h-[44px]"
      >
        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${ESTILO_TAREA[t.estado]}`}>
          {hecha ? <Check size={14} strokeWidth={3} /> : t.estado === 'vencida' ? <AlertTriangle size={13} strokeWidth={2.5} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block text-sm ${hecha ? 'text-slate-500 line-through' : 'font-semibold text-slate-800'}`}>{t.titulo}</span>
          <span className={`block text-xs ${t.estado === 'vencida' ? 'text-red-700' : 'text-slate-500'}`}>{t.detalle}</span>
        </span>
        {!hecha && t.avance != null && (
          <span className="hidden sm:block w-20 h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <span className="block h-full bg-amber-500" style={{ width: `${t.avance}%` }} />
          </span>
        )}
        {!hecha && (
          <span className="flex items-center gap-0.5 text-xs font-semibold text-primary-700 whitespace-nowrap">
            {t.ir}<ChevronRight size={15} />
          </span>
        )}
      </button>
    );
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">
          <Target size={20} className="text-turquoise-600" />
          Objetivos de {mes}
        </h2>
        {conTareas && total > 0 && (
          <span className="flex items-center gap-2.5 text-xs text-slate-600">
            <span><b className="text-slate-800">{listas.length} de {total}</b> tareas listas</span>
            <span className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
              <span className="block h-full bg-turquoise-600" style={{ width: `${Math.round((listas.length / total) * 100)}%` }} />
            </span>
          </span>
        )}
      </div>
      {conTareas && (<>
      <h3 className="text-xs font-bold text-slate-500 mb-1">
        Tareas del mes <span className="font-normal">· las calcula el sistema según tu rol</span>
      </h3>
      {tareas === null ? (
        <p className="py-4 text-sm text-slate-400">Cargando…</p>
      ) : total === 0 ? (
        <p className="py-4 text-sm text-slate-500">No hay tareas pendientes por ahora.</p>
      ) : (
        <div>
          {faltan.map(fila)}
          {faltan.length === 0 && <p className="py-3 border-t border-slate-100 text-sm font-medium text-turquoise-700">Todo al día.</p>}
          {listas.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setVerListas((v) => !v)}
                className="flex items-center gap-1 pt-2.5 border-t border-slate-100 w-full text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                <ChevronDown size={14} className={`transition-transform ${verListas ? 'rotate-180' : ''}`} />
                {verListas ? 'Ocultar' : 'Ver'} {listas.length === 1 ? 'la tarea ya lista' : `las ${listas.length} tareas ya listas`}
              </button>
              {verListas && <div className="mt-1">{listas.map(fila)}</div>}
            </>
          )}
        </div>
      )}
      </>)}
      <BloqueMetas datos={metas} onEditar={onEditarMetas} separado={conTareas} />
    </section>
  );
}

// Color y nota de una meta contra lo esperado a la fecha (día / días del mes):
// verde si va igual o mejor, ámbar hasta 10 puntos peor, rojo más allá. En el
// tope de compras, "mejor" es ir por debajo. El ticket promedio no se acumula:
// se compara contra el 100 %.
function estadoMeta(m, esperado) {
  const pct = m.valor > 0 ? ((m.lleva || 0) / m.valor) * 100 : 0;
  const ref = m.tipo === 'ticket_promedio' ? 100 : esperado;
  const atraso = m.tipo === 'tope_compras' ? pct - ref : ref - pct;
  const nivel = atraso <= 0 ? 'bien' : atraso <= 10 ? 'cerca' : 'mal';
  let nota;
  if (m.tipo === 'tope_compras') {
    nota = nivel === 'bien' ? 'Dentro del ritmo del mes'
      : `Ya se usó ${Math.round(pct)}% del tope y va el ${Math.round(esperado)}% del mes`;
  } else if (m.tipo === 'ticket_promedio') {
    nota = nivel === 'bien' ? 'En la meta o por encima' : nivel === 'cerca' ? 'Un poco por debajo de la meta' : 'Por debajo de la meta';
    if (m.tickets != null) nota += ` · ${plural(m.tickets, 'ticket', 'tickets')} este mes`;
  } else {
    nota = nivel === 'bien' ? 'Por encima de lo esperado a hoy' : nivel === 'cerca' ? 'Un poco por debajo de lo esperado a hoy' : 'Por debajo de lo esperado a hoy';
  }
  return { pct, nivel, nota };
}

const COLOR_META = {
  bien: { barra: 'bg-turquoise-600', texto: 'text-turquoise-700' },
  cerca: { barra: 'bg-amber-500', texto: 'text-amber-700' },
  mal: { barra: 'bg-red-600', texto: 'text-red-700' }
};

function BloqueMetas({ datos, onEditar, separado }) {
  const lista = datos?.metas || [];
  const fmt = (m, v) => (m.moneda === 'USD' ? `$${formatMonto(v || 0)}` : `BCV$ ${formatMonto(v || 0)}`);
  return (
    <div className={`flex flex-col gap-3.5 ${separado ? 'mt-4 pt-4 border-t border-slate-200' : ''}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-xs font-bold text-slate-500">
          Metas del mes <span className="font-normal">· las carga el gerente</span>
        </h3>
        {onEditar && datos && (
          <button type="button" onClick={onEditar} className="flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 min-h-[32px]">
            <Pencil size={13} /> {lista.length ? 'Editar metas' : 'Cargar metas'}
          </button>
        )}
      </div>
      {datos === null ? (
        <p className="text-sm text-slate-400">Cargando…</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-slate-500">
          {onEditar ? 'Todavía no hay metas para este mes. Cárgalas para ver aquí cómo va la empresa.' : 'Este mes no hay metas cargadas.'}
        </p>
      ) : (
        <>
          {lista.map((m) => {
            const e = estadoMeta(m, datos.esperado ?? 100);
            const c = COLOR_META[e.nivel];
            return (
              <div key={m.tipo} className="flex flex-col gap-1.5">
                <div className="flex flex-wrap justify-between gap-1.5 text-sm">
                  <span className="font-semibold text-slate-800">{NOMBRES_META[m.tipo].nombre}</span>
                  <span className="tabular-nums text-slate-600">
                    <b className="text-slate-800">{fmt(m, m.lleva)}</b> de {fmt(m, m.valor)} · <b className={c.texto}>{Math.round(e.pct)}%</b>
                  </span>
                </div>
                <div className="relative h-2.5 rounded-full bg-slate-200">
                  <span className={`absolute inset-y-0 left-0 rounded-full ${c.barra}`} style={{ width: `${Math.min(e.pct, 100)}%` }} />
                  {m.tipo !== 'ticket_promedio' && datos.esperado != null && (
                    <span title="Lo esperado a hoy" className="absolute -top-1 h-[18px] w-0.5 bg-slate-700" style={{ left: `${datos.esperado}%` }} />
                  )}
                </div>
                <span className={`text-xs ${c.texto}`}>{e.nota}</span>
              </div>
            );
          })}
          {datos.dia && (
            <p className="text-xs text-slate-500">La raya oscura marca lo esperado a la fecha: día {datos.dia} de {datos.dias_mes} del mes.</p>
          )}
        </>
      )}
    </div>
  );
}

function BloqueCaja({ datos }) {
  const cierre = datos?.cierre;
  const total = Number(datos?.total) || 0;
  const pctMostrador = total > 0 ? Math.round((datos.mostrador / total) * 100) : 0;
  return (
    <section className="rounded-2xl bg-navy-900 text-slate-200 p-4 sm:p-5 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-sm">
          {!datos ? (
            <span className="text-slate-400">Cargando la caja…</span>
          ) : cierre?.vencido ? (
            <>
              <AlertTriangle size={16} className="text-amber-400" />
              <span><b className="text-white">Turno del {formatFecha(cierre.fecha)} sin cerrar</b> · hay que cerrarlo para seguir vendiendo</span>
            </>
          ) : cierre ? (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-turquoise-400" />
              <span><b className="text-white">Caja abierta</b> · turno {cierre.turno}{cierre.abierto_en && ` desde las ${formatHora(cierre.abierto_en)}`}</span>
            </>
          ) : (
            <>
              <span className="h-2.5 w-2.5 rounded-full bg-slate-500" />
              <span><b className="text-white">Caja cerrada</b></span>
            </>
          )}
        </p>
        <Link
          to="/caja"
          className="flex items-center justify-center gap-2 rounded-xl bg-turquoise-500 hover:bg-turquoise-400 px-5 py-3 text-base font-bold text-navy-950 transition-colors"
        >
          <Store size={20} />
          {cierre ? 'Ir al punto de venta' : 'Abrir caja'}
        </Link>
      </div>
      {datos && (
        <>
          <div className="grid grid-cols-3 gap-3 tabular-nums">
            <Dato titulo="Cobrado hoy" valor={bcv(total)} />
            <Dato titulo="Tickets" valor={datos.tickets} />
            <Dato titulo="Ticket promedio" valor={bcv(datos.promedio)} />
          </div>
          {total > 0 && (
            <div className="space-y-1.5">
              <div className="flex h-2 overflow-hidden rounded-full bg-navy-800">
                <span className="bg-turquoise-300" style={{ width: `${pctMostrador}%` }} />
                <span className="bg-amber-400" style={{ width: `${100 - pctMostrador}%` }} />
              </div>
              <div className="flex justify-between text-xs text-slate-300">
                <span>Mostrador {pctMostrador}%</span>
                <span>Delivery {100 - pctMostrador}%</span>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Dato({ titulo, valor }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{titulo}</p>
      <p className="text-base sm:text-lg font-bold text-white">{valor}</p>
    </div>
  );
}
